import { Injectable } from '@angular/core';
import { AoxMeasurementResult } from 'src/app/interfaces/biozoom-api/aox-measurement-result';
import { HrvMeasurementResult } from 'src/app/interfaces/biozoom-api/hrv-measurement-result';
import { HrvTestMeasurementResult } from 'src/app/interfaces/biozoom-api/hrv-test-measurement-result';
import { PreciseAoxMeasurementResult } from 'src/app/interfaces/biozoom-api/precise-aox-measurement-result';

@Injectable({
  providedIn: 'root'
})
/**
 * generate mock-results for the demo-service
 */
export class DemoUtilsService {

  private readonly fallbackDemoSerial = '1E123456789012345678';
  private demoMeasurementNumber = 0;

  constructor() { }

  /**
   * generate an aox result for a quick aox measurement
   * @param additionalData if this contains 'AOX_extended_final' with the value 'true', add a precise result to the response object
   * @param serial serial ID submitted for the measurement
   * @returns object containing the results of a demo-aox measurement
   */
  genMeasurementAoxRequestDemo = (additionalData: any, serial?: string): AoxMeasurementResult => {
    const statusBefore = additionalData?.status_before ?? {};
    const statusAfter = additionalData?.status_after ?? {};
    const demoSerial = serial || statusBefore.serial || statusAfter.serial || this.fallbackDemoSerial;
    const result: AoxMeasurementResult & {status_before: any; status_after: any} = {
      ...(additionalData ?? {}),
      // A real VWS response reflects the scanner context submitted with the
      // raw measurement. A precise series requires this serial to be present
      // and identical for every single result.
      status_before: {...statusBefore, serial: demoSerial},
      status_after: {...statusAfter, serial: demoSerial},
      demo: true,
      aox_q: 40,
      aox: 4.1,
      aox_int_result: 4,
      nextPage: 4,
      quality: 0,
      result: 4,
      uuid: `demo-${++this.demoMeasurementNumber}`,
      url: 'http://biozoom.org/' +
        'en_r4.html?' +
        'q=0&' +
        'serial=1E123456789012345678&' +
        'UserID=ffffffffffffffffffffffffffffffff&' +
        'result=4.1&' +
        'aox=4.1&'

    };
    if (additionalData && additionalData.AOX_extended_final === 'yes') {
      result.precise_aox = '4,3';
    }
    return result;
  }

  /**
   * generate an aox result for a precise aox measurement
   * @returns object containing the results of a demo-aox measurement
   */
  genMeasurementAoxPreciseRequestDemo = (): PreciseAoxMeasurementResult => {
    return {
      demo: true,
      aox_q: 40,
      aox: 4.1,
      aox_int_result: 4,
      nextPage: 4,
      quality: 0,
      result: 4,
      precise_aox: 4.3,
      precise_aox_floor_result: 4,
      precise_aox_std: 0.27,
      precise_measurements_std: 0.0,
      precise_quality_hand: 3,
      precise_quality_result: 3,
      url: 'http://biozoom.org/' +
        'en_r4.html?' +
        'q=0&' +
        'serial=1E123456789012345678&' +
        'UserID=ffffffffffffffffffffffffffffffff&' +
        'result=4.1&' +
        'aox=4.1&' +
        'std_a=0.27&std_m=0.0'
    };
  }

  /**
   * generate a hrv result for a normal measurement
   * @returns object containing the results of a demo-hrv measurement
   */
  genMeasurementHrvRequestDemo = (): HrvMeasurementResult => {
    return {
      demo: true,
      std_nn: '63 - 73',
      mean_hr: 81.3,
      y3: 50,
      y2: 50,
      y4: 50,
      y6: 50,
      bmi_age_gender_category: 2,
      std_nn_raw: 67.60723516458096,
      nextPage: '0',
      hrv_report_url: 'https://server.biozoom.org/vitality-report/overview?values=eyJibWlfYWdlX2dlbmRlcl9jYXRlZ29yeSI6Miwic3RkX25uIjoiNjMgLSA3MyIsImhydl9iaW9fYWdlX2NhdGVnb3J5IjozLCJocnZfYWdlX2NhdGVnb3J5IjozLCJwcm9kdWN0X2oyYmYiOiJDIiwidGltZXN0YW1wIjoxNjE5NDM1NTQ2LCJibWkiOjIxLjMsImhydl9iaW9fYWdlIjoiMjEtMzEiLCJtZWFuX2hyIjo4MS4zLCJhb3hfaW50X3Jlc3VsdCI6NSwieDMiOjQ1LCJ4MiI6NTguMTYsIngiOjUxLjg0LCJzZXJpYWwiOiIxRTEyMzQ1Njc4OTAxMjM0NTY3OCIsImhyX2FnZV9nZW5kZXJfY2F0ZWdvcnkiOjMsIng2IjoxMCwieDQiOjM0LjYyLCJhb3giOjUuNCwiZGVtbyI6dHJ1ZX0',
      hrv_q: 0,
      hrv_bio_age_category: 3,
      hrv_age_category: 3,
      bmi: 21.3,
      aox_q: 5,
      x2: 58.16,
      x3: 45,
      x6: 10,
      x4: 34.62,
      aox: 5.4,
      hr_raw: 81.31302948572124,
      url: '',
      hrv_bio_age: '21-31',
      aox_int_result: 5,
      hr_age_gender_category: 3,
      y: 50,
      x: 51.84,
      product_j2bf: 'B',
    };
  }

  /**
   * generate a hrv result for a normal measurement
   * @returns object containing the results of a demo-hrv measurement without the aox value
   */
  genMeasurementHrvRequestDemoWithoutAoxValue = (): HrvMeasurementResult => {
    return {
      demo: true,
      std_nn: '63 - 73',
      mean_hr: 81.3,
      y3: 50,
      y2: 50,
      y4: 50,
      y6: 50,
      bmi_age_gender_category: 2,
      std_nn_raw: 67.60723516458096,
      nextPage: '0',
      hrv_report_url: 'https://server.biozoom.org/vitality-report/overview?values=eyJibWlfYWdlX2dlbmRlcl9jYXRlZ29yeSI6Miwic3RkX25uIjoiNjMgLSA3MyIsImhydl9iaW9fYWdlX2NhdGVnb3J5IjozLCJocnZfYWdlX2NhdGVnb3J5IjozLCJwcm9kdWN0X2oyYmYiOiJDIiwidGltZXN0YW1wIjoxNjE5NDM1NTQ2LCJibWkiOjIxLjMsImhydl9iaW9fYWdlIjoiMjEtMzEiLCJtZWFuX2hyIjo4MS4zLCJhb3hfaW50X3Jlc3VsdCI6NSwieDMiOjQ1LCJ4MiI6NTguMTYsIngiOjUxLjg0LCJzZXJpYWwiOiIxRTEyMzQ1Njc4OTAxMjM0NTY3OCIsImhyX2FnZV9nZW5kZXJfY2F0ZWdvcnkiOjMsIng2IjoxMCwieDQiOjM0LjYyLCJhb3giOjUuNCwiZGVtbyI6dHJ1ZX0',
      hrv_q: 0,
      hrv_bio_age_category: 3,
      hrv_age_category: 3,
      // bmi: 21.3,
      aox_q: 5,
      x2: 58.16,
      x3: 45,
      x6: 10,
      x4: 34.62,
      // aox: 5.4,
      hr_raw: 81.31302948572124,
      url: '',
      // hrv_bio_age: '21-31',
      aox_int_result: 5,
      hr_age_gender_category: 3,
      y: 50,
      x: 51.84,
      product_j2bf: 'B',
    };
  }

  /**
   * generate a hrv result for a test measurement
   * @returns object containing the results of a demo-hrv test measurement
   */
  genMeasurementHrvTestRequestDemo = (): HrvTestMeasurementResult => {
    return {
      nextPage: '',
      hand_drauf: '1',
      hand_score: '100'
    };
  }
}
